// Personal BYOK keys data layer (per-user provider cascade, epic #635). Same
// masked-row shape as the org pool (`lib/keys.ts`), but scoped to the ACTING
// user within the active account — reads/writes `/v1/me/keys`. A member's own
// keys rank ahead of the org's shared pool; the org pool stays the fallback.
// Unlike the org list, this view also drives verify (`POST /:id/verify`).

import { createQuery } from '@tanstack/solid-query';
import { activeAccountId } from './active-account';
import { apiFetch } from './api';
import { type KeyCaptureAnswer, parseKeyCapture } from './key-capture';
import { type KeyVerifyAnswer, parseKeyVerify } from './key-verify';
import {
  type AddKeyInput,
  type KeyRow,
  type ProbeModelsInput,
  type ProviderModelOption,
  probeModelsAt,
} from './keys';
import type { Page } from './paged';
import { SESSION_TOKEN } from './session';

export type {
  AddKeyInput,
  AddKeyModelInput,
  KeyModelRow,
  KeyRow,
  ProbeModelsInput,
  ProviderModelOption,
} from './keys';

/**
 * Enumerate a raw key's models for the PERSONAL pool (arc 101 slice 02). Same
 * body, same `Page<{id, context_length?}>` answer — the provider-reported window
 * rides the rows here exactly as it does for the org pool — and the same
 * refusals. A different ROUTE because it authorizes a different principal:
 * `write:self:keys` rather than the admin-only `write:billing`, so a plain
 * member can capture their own key. The framing lives in
 * `lib/keyProbeRefusal.ts`, once for both.
 */
export function probeMyProviderModels(
  input: ProbeModelsInput,
  token: string | null = SESSION_TOKEN,
): Promise<readonly ProviderModelOption[]> {
  return probeModelsAt('/v1/me/keys/models', input, token);
}

export const MY_KEYS_QUERY_KEY = 'my_keys';

/** `useMyKeys`'s cache key — also what the priority edit patches optimistically. */
export const myKeysQueryKey = (token: string | null): readonly unknown[] => [
  MY_KEYS_QUERY_KEY,
  token,
  activeAccountId(),
];

/**
 * The acting user's personal keys. Disabled (idle) with no session, like
 * `useKeys`. Revoked keys are dropped (revoke = remove), matching the org list.
 *
 * The wire is the canonical `{data, next_cursor}` envelope with `next_cursor`
 * always null — a person's own keys are served whole — so this is a plain query,
 * never `usePagedQuery`, and there is no Load-more to offer.
 */
export function useMyKeys(token: string | null = SESSION_TOKEN) {
  return createQuery<readonly KeyRow[]>(() => ({
    queryKey: myKeysQueryKey(token),
    enabled: token !== null,
    queryFn: async () =>
      (await apiFetch<Page<KeyRow>>('/v1/me/keys', token)).data.filter(
        (k) => k.status !== 'revoked',
      ),
  }));
}

/**
 * Capture a raw personal key. The 201 is the masked row AFTER the inline verify
 * plus its `verification` (plan 102 slice 05, `lib/key-capture.ts`).
 */
export async function addMyKey(
  input: AddKeyInput,
  token: string | null = SESSION_TOKEN,
): Promise<KeyCaptureAnswer> {
  return parseKeyCapture(
    await apiFetch<unknown>('/v1/me/keys', token, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  );
}

/**
 * Check a personal key against its provider. Returns the 200's `{ outcome,
 * reason }` (`lib/key-verify.ts`: only `verified` is a pass, a body without one
 * throws); a 422 (`{...row, reason}`) throws `ApiError` — the caller refetches
 * and the card reflects the persisted `invalid` status.
 */
export async function verifyMyKey(
  id: string,
  token: string | null = SESSION_TOKEN,
): Promise<KeyVerifyAnswer> {
  return parseKeyVerify(
    await apiFetch<unknown>(`/v1/me/keys/${id}/verify`, token, { method: 'POST' }),
  );
}

/** Soft-revoke a personal key. Returns the revoked row. */
export async function revokeMyKey(
  id: string,
  token: string | null = SESSION_TOKEN,
): Promise<KeyRow> {
  return apiFetch<KeyRow>(`/v1/me/keys/${id}`, token, { method: 'DELETE' });
}

/**
 * Swap a personal key's raw material in place (`POST /:id/rotate`, #971).
 * Same contract as `rotateKey`: 200 → the masked row (status `valid`); a 422
 * `detail` names the provider refusal and the OLD key stays live.
 */
export async function rotateMyKey(
  id: string,
  input: { readonly key: string },
  token: string | null = SESSION_TOKEN,
): Promise<KeyRow> {
  return apiFetch<KeyRow>(`/v1/me/keys/${id}/rotate`, token, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/**
 * Re-rank a personal key within the member's own pool (`PATCH /:id/priority`,
 * plan 102 slice 05b; `write:self:keys`). Same contract as `setKeyPriority`.
 */
export async function setMyKeyPriority(
  id: string,
  priority: number,
  token: string | null = SESSION_TOKEN,
): Promise<KeyRow> {
  return apiFetch<KeyRow>(`/v1/me/keys/${id}/priority`, token, {
    method: 'PATCH',
    body: JSON.stringify({ priority }),
  });
}
